import React, { useState } from "react";
import { CheckCircle, XCircle, UserCircleGear } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { C } from "./shared";

const FIX_TAB = {
  profile: null, revenue_drivers: "assumptions", cost_structure: "assumptions",
  balance: "balance", cash: "cashflow", growth: "assumptions", history: "historicals",
};

export default function CrsCheck({ out, setTab, preRevenue, model, fmId }) {
  const crs = out.crs;
  const good = crs.score >= 80;
  return (
    <div data-testid="fm-crs" className="max-w-3xl space-y-5">
      <div className="text-xs text-white/45">
        Section K · CRS self-assessment — the same integrity checks a Clinq analyst runs before a model goes in front of a funder.
      </div>
      <div className="rounded-xl p-5 flex items-center gap-5" style={{ backgroundColor: C.panel, border: `1px solid ${good ? "rgba(44,197,168,0.4)" : "rgba(245,158,11,0.4)"}` }}>
        <div className="relative flex items-center justify-center" style={{ width: 84, height: 84 }}>
          <svg viewBox="0 0 84 84" width="84" height="84">
            <circle cx="42" cy="42" r="36" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
            <circle cx="42" cy="42" r="36" fill="none" stroke={good ? C.green : C.orange} strokeWidth="7"
              strokeDasharray={`${(crs.score / 100) * 226} 226`} strokeLinecap="round" transform="rotate(-90 42 42)" />
          </svg>
          <div className="absolute text-lg font-extrabold text-white" data-testid="fm-crs-score">{crs.score}</div>
        </div>
        <div>
          <div className="text-lg font-extrabold" style={{ color: good ? C.green : C.orange }} data-testid="fm-crs-band">{crs.band}</div>
          <div className="text-xs text-white/50 mt-1">{crs.passed} of {crs.total} checks passing. {good ? "This model would clear a Clinq analyst's first pass." : "Clear the failing checks below before sharing with funders."}</div>
        </div>
      </div>
      <div className="space-y-2">
        {crs.checks.map((c) => (
          <div key={c.id} className="rounded-lg px-4 py-3 flex items-start gap-3" data-testid={`fm-crs-check-${c.id}`}
            style={{ backgroundColor: C.panel, border: `1px solid ${c.passed ? "rgba(44,197,168,0.18)" : "rgba(248,113,113,0.3)"}` }}>
            {c.passed
              ? <CheckCircle size={20} weight="fill" style={{ color: C.green, marginTop: 1, flexShrink: 0 }} />
              : <XCircle size={20} weight="fill" style={{ color: C.red, marginTop: 1, flexShrink: 0 }} />}
            <div className="flex-1">
              <div className="text-sm font-bold text-white">{c.label}</div>
              <div className="text-[11px] text-white/45 mt-0.5">{c.detail}</div>
            </div>
            {!c.passed && FIX_TAB[c.id] && (
              <button onClick={() => setTab(c.id === "history" && preRevenue ? "milestones" : FIX_TAB[c.id])}
                data-testid={`fm-crs-fix-${c.id}`}
                className="text-[11px] font-bold px-3 py-1.5 rounded-md whitespace-nowrap"
                style={{ backgroundColor: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", color: C.orange }}>
                Fix it →
              </button>
            )}
          </div>
        ))}
      </div>
      {model && fmId && <AdvisorReviewCard model={model} fmId={fmId} />}
    </div>
  );
}

function AdvisorReviewCard({ model, fmId }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fm/models/${fmId}/review/checkout`, {
        currency: "USD", callback_url: `${window.location.origin}/payment/callback`,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl p-5 flex items-start gap-4" data-testid="fm-review-card"
      style={{ backgroundColor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.3)" }}>
      <UserCircleGear size={28} weight="duotone" style={{ color: C.orange, flexShrink: 0, marginTop: 2 }} />
      <div className="flex-1">
        <div className="text-sm font-extrabold text-white">Want a senior advisor to review this model?</div>
        <p className="text-xs text-white/50 mt-1">
          A Clinq advisor stress-tests your assumptions against sector benchmarks, helps you clear the failing checks, and positions the model for your target funder — on a call within one business day.
        </p>
        {model.advisor_review_paid_at ? (
          <div className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold px-3 py-1.5 rounded-full"
            data-testid="fm-review-requested"
            style={{ backgroundColor: "rgba(44,197,168,0.15)", color: C.green }}>
            <CheckCircle size={14} weight="fill" /> Advisor review requested — we'll reach out within one business day
          </div>
        ) : (
          <button onClick={start} disabled={busy} data-testid="fm-review-btn"
            className="mt-3 rounded-md px-4 py-2.5 text-xs font-bold disabled:opacity-50"
            style={{ backgroundColor: C.orange, color: "#000" }}>
            {busy ? "Starting checkout…" : "Send to advisor for review · $175"}
          </button>
        )}
      </div>
    </div>
  );
}
